import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as XLSX from 'xlsx';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableContainer,
  TableHead,
  TableRow as MuiTableRow,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  MenuItem,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  FormGroup
} from '@mui/material';

import { borderBottom, styled } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import PrimaryButton from '../../ui/buttons/primaryButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { TableSortLabel } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from '../../ui/forms/fields/input';
import {
  addAdvisor,
  deleteAdvisor,
  getAdvisors,
  inviteAdvisor,
  updateAdvisor
} from '../../../features/advisor/advisor.slice';
import { getAllRoles } from '../../../features/role/role.slice';

const TableCell = styled(MuiTableCell)(({ theme }) => ({
  '&:last-child': {
    padding: theme.spacing(1)
  },
  borderBottom: 'none'
}));

const TableRow = styled(MuiTableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover
  },
  '&:first-of-type': {
    '& th': {
      borderBottom: `2px solid ${theme.palette.divider}`
    }
  }
}));

const schema = yup.object().shape({
  firstName: yup.string().required('Name is required').min(2, 'Name is too short'),
  lastName: yup.string().required('Surname is required').min(2, 'Surname is too short'),
  email: yup.string().email('Invalid email format').required('Email is required')
});

function HouseAdvisors() {
  const dispatch = useDispatch();
  const advisors = useSelector((state) => state.advisor.advisors);
  const loadingAdvisors = useSelector((state) => state.advisor.loadingAdvisors);
  const loadingInvite = useSelector((state) => state.advisor.loading);

  const [loading, setLoading] = useState(false);
  const [invitingAdvisorId, setInvitingAdvisorId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAdvisor, setCurrentAdvisor] = useState({ firstName: '', lastName: '', email: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [advisorToDelete, setAdvisorToDelete] = useState(null);
  const [progress, setProgress] = React.useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [file, setFile] = useState(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur'
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const isSameKey = prev.key === key;
      const newDirection = isSameKey && prev.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction: newDirection };
    });
  };

  const sortedAdvisors = [...advisors].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const valA = a[sortConfig.key].toLowerCase();
    const valB = b[sortConfig.key].toLowerCase();

    if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
    if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  useEffect(() => {
    dispatch(getAdvisors());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllRoles());
  }, [dispatch]);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === 100) {
          return 0;
        }
        const diff = Math.random() * 10;
        return Math.min(oldProgress + diff, 100);
      });
    }, 500);

    return () => {
      clearInterval(timer);
    };
  }, []);

  const downLoadUrl =
    'https://s3.af-south-1.amazonaws.com/s3.fairtree.public/portal/Advisor+Profiles.xlsx';

  const handleOpenDialog = (advisor = { firstName: '', lastName: '', email: '', roles: [] }) => {
    setCurrentAdvisor(advisor);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentAdvisor({ firstName: '', lastName: '', email: '' });
  };

  const handleSaveAdvisor = (data) => {
    const newAdvisor = { ...data, _id: currentAdvisor._id || Date.now() };
    if (currentAdvisor._id) {
      dispatch(updateAdvisor(newAdvisor))
        .unwrap()
        .then(() => {
          dispatch(getAdvisors());
        });
    } else {
      dispatch(addAdvisor(newAdvisor))
        .unwrap()
        .then(() => {
          dispatch(getAdvisors());
        });
    }
    handleCloseDialog();
  };

  const handleDeleteAdvisor = (id) => {
    setAdvisorToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteAdvisor = () => {
    dispatch(deleteAdvisor(advisorToDelete))
      .unwrap()
      .then(() => {
        dispatch(getAdvisors());
      });
    setDeleteDialogOpen(false);
    setAdvisorToDelete(null);
  };
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      parseExcel(file);
    }
  };

  const handleInvite = async (id) => {
    try {
      setInvitingAdvisorId(id);
      await dispatch(inviteAdvisor(id)).unwrap();
    } catch (error) {
      console.error('Error inviting advisor:', error);
    } finally {
      setInvitingAdvisorId(null);
    }
  };

  const parseExcel = (file) => {
    if (!file) {
      console.error('No file provided for parsing.');
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const binaryString = e.target.result;
        const workbook = XLSX.read(binaryString, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          console.error('No sheets found in the workbook.');
          return;
        }

        const sheet = workbook.Sheets[sheetName];
        const advisorsData = XLSX.utils.sheet_to_json(sheet);

        const generateUniqueId = (index) => `${Date.now()}_${index}`;

        const newAdvisors = advisorsData.map((advisor, index) => ({
          id: generateUniqueId(index),
          firstName: (advisor['Advisor First Name'] || '').trim(),
          lastName: (advisor['Advisor Last Name'] || '').trim(),
          email: (advisor['Advisor Email'] || '').trim()
        }));

        newAdvisors.forEach((advisor) => {
          if (advisor.firstName && advisor.lastName && advisor.email) {
            dispatch(addAdvisor(advisor))
              .unwrap()
              .then(() => {
                dispatch(getAdvisors());
              });
          } else {
            console.warn('Invalid advisor data:', advisor);
          }
        });

        dispatch(getAdvisors());
      } catch (error) {
        console.error('Error parsing Excel file:', error);
      }
    };

    reader.onerror = (error) => {
      console.error('File reading error:', error);
    };

    reader.readAsBinaryString(file);
  };

  return (
    <Box p={3}>
      <TableContainer component={Paper} sx={{ padding: 3, borderRadius: 5 }}>
        <Box mb={3}>
          <Typography variant="h2" fontWeight={500} gutterBottom>
            Advisor Profiles
          </Typography>
          <Typography variant="subtitle1" fontSize={'20px'}>
            Please add the first name, last name, and email address of your advisors:
          </Typography>
        </Box>
        <Box alignItems={'center'} display="flex" justifyContent="flex-end" gap={2} mb={2}>
          <PrimaryButton
            href={downLoadUrl}
            sx={{
              minWidth: '200px',
              borderRadius: 2,
              height: '40px',
              backgroundColor: '#000000',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#333'
              }
            }}
            onClick={() => {
              setLoading(true);
              setTimeout(() => {
                setLoading(false);
              }, 3000);
            }}>
            {loading ? 'Downloading Template...' : 'DOWNLOAD TEMPLATE'}
          </PrimaryButton>

          <Button
            variant="contained"
            component="label"
            sx={{
              width: '200px',
              borderRadius: 2,
              height: '40px',
              backgroundColor: '#CC6323',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#9A4D19'
              }
            }}>
            UPLOAD TEMPLATE
            <input type="file" accept=".xlsx" hidden onChange={handleFileChange} />
          </Button>

          <IconButton color="primary" onClick={() => handleOpenDialog()}>
            <AddCircleOutlineIcon sx={{ fontSize: 40 }} />
          </IconButton>
        </Box>
        {loadingAdvisors && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              width: '100%',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#CC6323'
              },
              backgroundColor: '#F1A46E'
            }}
          />
        )}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'firstName'}
                  direction={sortConfig.direction}
                  onClick={() => handleSort('firstName')}>
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'lastName'}
                  direction={sortConfig.direction}
                  onClick={() => handleSort('lastName')}>
                  Surname
                </TableSortLabel>
              </TableCell>
              <TableCell>Email</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedAdvisors.length > 0 &&
              sortedAdvisors.map((advisor) => (
                <TableRow key={advisor.id}>
                  <TableCell>{advisor.firstName}</TableCell>
                  <TableCell>{advisor.lastName}</TableCell>
                  <TableCell>{advisor.email}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenDialog(advisor)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteAdvisor(advisor._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => handleInvite(advisor._id)}
                      sx={{ minWidth: 120 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%'
                        }}>
                        <Box sx={{ width: 16, height: 16, visibility: 'hidden' }}>
                          <CircularProgress size={15} />
                        </Box>

                        <Box sx={{ flexShrink: 0, textAlign: 'center' }}>Invite</Box>

                        <Box sx={{ width: 16, height: 16 }}>
                          <CircularProgress
                            size={15}
                            color="accent"
                            sx={{
                              visibility: invitingAdvisorId === advisor._id ? 'visible' : 'hidden'
                            }}
                          />
                        </Box>
                      </Box>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        sx={{ padding: 3, borderRadius: '50px' }}
        fullWidth>
        <Box sx={{ position: 'relative' }}>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500]
            })}>
            <CloseIcon sx={{ fontSize: 30 }} />
          </IconButton>
          <DialogContent sx={{ mt: 5 }}>
            <Input
              margin="dense"
              label="Name"
              fullWidth
              {...register('firstName')}
              value={currentAdvisor.firstName}
              onChange={(e) => setCurrentAdvisor({ ...currentAdvisor, firstName: e.target.value })}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />

            <Input
              margin="dense"
              label="Surname"
              fullWidth
              {...register('lastName')}
              value={currentAdvisor.lastName}
              onChange={(e) => setCurrentAdvisor({ ...currentAdvisor, lastName: e.target.value })}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
            />

            <Input
              margin="dense"
              label="Email"
              fullWidth
              {...register('email')}
              value={currentAdvisor.email}
              onChange={(e) => setCurrentAdvisor({ ...currentAdvisor, email: e.target.value })}
              error={!!errors.email}
              helperText={errors.email?.message}
            />

            {errors.roles && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {errors.roles?.message}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={handleSubmit(handleSaveAdvisor)}>
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this adivisor profile? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button sx={{ color: '#CC6323' }} onClick={confirmDeleteAdvisor}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HouseAdvisors;
